export interface DropResolution {
  sourceGroupTitle: string
  columnTitle: string
  destGroupTitle: string
  overColumnTitle?: string
}

export interface ResolveArgs {
  keys: string[] // Header cells only, in DOM order
  from: number // dragIndex (0..len-1)
  slot: number // drop slot (0..len), computed by DnD
}

export const parseColumnKey = (key: string) => {
  const dash = key.indexOf("-")
  if (dash === -1) return { groupTitle: "", colTitle: key }
  return {
    groupTitle: key.slice(0, dash),
    colTitle: key.slice(dash + 1),
  }
}

/**
 * Pure translation from raw indices to semantic reorder intent.
 */
export function resolveColumnDrop({
  keys,
  from,
  slot,
}: ResolveArgs): DropResolution | null {
  const len = keys.length
  if (len === 0 || from < 0 || from >= len) return null

  const clampedSlot = Math.max(0, Math.min(slot, len))

  const parseAt = (i: number) =>
    i >= 0 && i < len ? parseColumnKey(keys[i]) : undefined
  const groupAt = (i: number) => parseAt(i)?.groupTitle
  const colAt = (i: number) => parseAt(i)?.colTitle

  const { groupTitle: srcGroup, colTitle: srcCol } = parseColumnKey(keys[from])

  // Append to end (inside last group)
  if (clampedSlot === len) {
    const lastGroup = groupAt(len - 1)
    // Accept "" (ungrouped) as a valid group id
    if (lastGroup === undefined) return null
    return {
      sourceGroupTitle: srcGroup,
      columnTitle: srcCol,
      destGroupTitle: lastGroup,
    }
  }

  // Determine boundary context around the slot
  const leftIdx = clampedSlot - 1
  const rightIdx = clampedSlot

  const leftGroup = leftIdx >= 0 ? groupAt(leftIdx) : undefined
  const rightGroup = rightIdx < len ? groupAt(rightIdx) : undefined
  const rightCol = colAt(rightIdx)

  const atBoundary = leftGroup !== rightGroup
  const isRightward = clampedSlot > from

  if (!atBoundary) {
    // Insert before the rightIdx column so order is stable
    if (rightGroup === undefined || rightCol === undefined) return null
    return {
      sourceGroupTitle: srcGroup,
      columnTitle: srcCol,
      destGroupTitle: rightGroup,
      overColumnTitle: rightCol,
    }
  }

  // Boundary rules:
  // - Prefer staying in source if one side is the source group
  // - Otherwise, prefer the group in the direction of movement
  const boundaryIncludesSource =
    (leftGroup !== undefined && leftGroup === srcGroup) ||
    (rightGroup !== undefined && rightGroup === srcGroup)

  if (boundaryIncludesSource) {
    if (leftGroup !== undefined && leftGroup === srcGroup) {
      // Stay in source on left side (append to end of source)
      return {
        sourceGroupTitle: srcGroup,
        columnTitle: srcCol,
        destGroupTitle: srcGroup,
      }
    }
    if (
      rightGroup !== undefined &&
      rightGroup === srcGroup &&
      rightCol !== undefined
    ) {
      // Stay in source on right side (insert at start of source)
      return {
        sourceGroupTitle: srcGroup,
        columnTitle: srcCol,
        destGroupTitle: srcGroup,
        overColumnTitle: rightCol,
      }
    }
  }

  // Prefer movement direction
  if (isRightward && rightGroup !== undefined && rightCol !== undefined) {
    return {
      sourceGroupTitle: srcGroup,
      columnTitle: srcCol,
      destGroupTitle: rightGroup,
      overColumnTitle: rightCol,
    }
  }
  if (!isRightward && leftGroup !== undefined) {
    return {
      sourceGroupTitle: srcGroup,
      columnTitle: srcCol,
      destGroupTitle: leftGroup,
    }
  }

  // Fallbacks
  if (leftGroup !== undefined) {
    return {
      sourceGroupTitle: srcGroup,
      columnTitle: srcCol,
      destGroupTitle: leftGroup,
    }
  }
  if (rightGroup !== undefined && rightCol !== undefined) {
    return {
      sourceGroupTitle: srcGroup,
      columnTitle: srcCol,
      destGroupTitle: rightGroup,
      overColumnTitle: rightCol,
    }
  }

  return null
}
